import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { Dialog } from '@/components/Base/Headless';
import {
  deleteAdminUser,
  fetchAdminUserList,
  type AdminUserRow,
} from '@/api/adminUsers';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Main() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchAdminUserList();
      setUsers(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const openDeleteDialog = (row: AdminUserRow) => {
    setDeleteError('');
    setDeleteTarget(row);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAdminUser(deleteTarget.id);
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete admin user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
              Manage Users
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Add and manage admin portal users
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            className="w-full sm:w-auto font-semibold uppercase tracking-wide"
            onClick={() => navigate('/manage-users/add')}
          >
            <Lucide icon="Plus" className="stroke-[1.3] w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 shadow-sm overflow-hidden">
        {error && (
          <div className="mx-6 mt-5 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200/80 dark:border-darkmode-400 bg-slate-50/80 dark:bg-darkmode-400/30">
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Name
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Username
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Email
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Role
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Created
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500 text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    <Lucide icon="Loader" className="w-5 h-5 mx-auto mb-2 animate-spin" />
                    Loading users…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    No admin users found
                  </td>
                </tr>
              ) : (
                users.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-slate-100 dark:border-darkmode-400 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-darkmode-400/20"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                          {getInitials(row.name)}
                        </div>
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {row.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-600 dark:text-slate-300">{row.username}</td>
                    <td className="px-4 py-4 text-slate-600 dark:text-slate-300">{row.email}</td>
                    <td className="px-4 py-4">
                      {row.isSuperAdmin ? (
                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium bg-violet-100 text-violet-700">
                          Super Admin
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600">
                          Admin
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {dayjs(row.createdAt).format('D MMM YYYY')}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {row.isSuperAdmin ? (
                        <span className="text-xs text-slate-400">Protected</span>
                      ) : (
                        <div className="inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-primary transition-colors"
                            aria-label={`Edit ${row.name}`}
                            onClick={() => navigate(`/manage-users/${row.id}/edit`)}
                          >
                            <Lucide icon="Pencil" className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-2 text-slate-400 hover:bg-danger/10 hover:text-danger transition-colors"
                            aria-label={`Delete ${row.name}`}
                            onClick={() => openDeleteDialog(row)}
                          >
                            <Lucide icon="Trash2" className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && users.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-slate-200/80 dark:border-darkmode-400 px-6 py-4 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing 1 to {users.length} of {users.length} entries
            </span>
          </div>
        )}
      </div>

      <Dialog open={deleteTarget !== null} onClose={closeDeleteDialog} size="sm">
        <Dialog.Panel>
          <Dialog.Title>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Delete Admin User
            </h3>
          </Dialog.Title>
          <Dialog.Description>
            <p className="text-slate-600 dark:text-slate-300">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {deleteTarget?.name}
              </span>{' '}
              ({deleteTarget?.username})?
            </p>
            <p className="mt-3 text-sm text-danger">
              This action cannot be reversed. The user will no longer be able to sign in to the
              admin portal.
            </p>
            {deleteError && (
              <div className="mt-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
                {deleteError}
              </div>
            )}
          </Dialog.Description>
          <Dialog.Footer className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline-primary"
              onClick={closeDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => void handleConfirmDelete()}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Confirm Deletion'
              )}
            </Button>
          </Dialog.Footer>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
}

export default Main;
